import { Button, Input, Modal, Select, useToast } from '@/components';
import { useUsers } from '@/features/users/hooks';
import { ApiClientError } from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';
import { useAssignDevice } from './hooks';

interface Props {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  currentAssigneeId: string | null;
}

export const AssignDeviceModal = (props: Props) => {
  const users = useUsers(() => ({}));
  const assign = useAssignDevice(props.deviceId);
  const toast = useToast();
  const [userId, setUserId] = createSignal(props.currentAssigneeId ?? '');
  const [note, setNote] = createSignal('');
  const [error, setError] = createSignal<string | null>(null);

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    if (!userId()) {
      setError('Select a user');
      return;
    }
    try {
      await assign.mutateAsync({ userId: userId(), note: note() || undefined });
      toast.success('Device assigned');
      props.onClose();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.error.message);
      } else {
        setError((err as Error).message ?? 'Assign failed');
      }
    }
  };

  return (
    <Modal open={props.open} onClose={props.onClose} title="Assign device">
      <form onSubmit={onSubmit} class="flex flex-col gap-4">
        <Select
          label="User"
          value={userId()}
          onChange={setUserId}
          placeholder="Select a user"
          required
          options={(users.data?.data ?? []).map((u) => ({
            value: u.id,
            label: `${u.displayName || u.email}${u.status === 'pending' ? ' (pending)' : ''}`,
          }))}
        />
        <Show when={props.currentAssigneeId && props.currentAssigneeId !== userId()}>
          <p class="text-xs text-amber-600">Reassigning will end the current assignment.</p>
        </Show>
        <Input
          label="Note (optional)"
          value={note()}
          onInput={(e) => setNote(e.currentTarget.value)}
        />
        <Show when={error()}>
          <p class="text-xs text-red-600">{error()}</p>
        </Show>
        <div class="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={assign.isPending}>
            Assign
          </Button>
        </div>
      </form>
    </Modal>
  );
};
